// src/utils/manuscriptStructure.js
//
// The two decisions behind the Manuscript Structure page that are pure functions
// of the draft, kept out of the page component so they can be tested without
// standing up a router and a theme.
//
// See `src/pages/ManuscriptStructure.jsx` for the page itself and
// `src/config/manuscriptElements.js` for the element catalogue.

import { MANUSCRIPT_ELEMENTS, defaultStructureState } from '../config/manuscriptElements';
import { getHeadingBlocks } from './outline';

/**
 * Where the Cover Designer button goes, or null if there is no cover to open.
 *
 * "Has a cover assigned" and "where the button goes" are deliberately the same
 * question, so the button can never be enabled without somewhere to send you —
 * a null path is what disables it.
 *
 * Two shapes count, because the app supports both: a manuscript carrying its own
 * cover versions, and one linked to a separate cover-only draft. A rendered
 * thumbnail (`coverImage` / `cover_url`) is **not** the test — a cover can exist
 * with a blank canvas, and a stale thumbnail can outlive the cover it came from.
 */
export function coverDesignerPath(draft) {
  if (!draft) return null;

  // The server says which draft holds the cover (`effective_cover_draft_id`).
  // Asking it, rather than deciding here, is what stops this button and the
  // Book Preview opening two different covers for the same book — which they
  // did, for any manuscript that had both its own versions and a link.
  const coverDraftId = draft.effectiveCoverDraftId;
  if (coverDraftId && coverDraftId !== String(draft._id)) {
    return `/bookcover/${coverDraftId}/designer`;
  }

  const versions = draft.coverVersions || [];
  if (versions.length) {
    const activeId = draft.activeCoverVersionId || versions[0]?.id;
    return activeId
      ? `/bookcover/${draft._id}/designer/${activeId}`
      : `/bookcover/${draft._id}/designer`;
  }

  // A linked cover lives in its own draft, whose versions this payload does not
  // carry — so link at the designer and let it open that draft's active version.
  if (draft.linkedCoverId) return `/bookcover/${draft.linkedCoverId}/designer`;

  return null;
}

/**
 * Keys where a stored empty string means "never set" rather than a real choice.
 *
 * The endpoint always writes the whole format object, so an element saved before
 * typography existed — or by a client that does not send it — comes back with
 * `fontFamily: ""`. Spread naively, that empty string beats the catalogue default
 * and the element silently renders in the fallback face with a blank font select.
 *
 * `textStyle` is deliberately not in here: empty is a real value there, the one
 * the toolbar shows as "Custom" once you adjust a control the preset owned.
 */
const FORMAT_KEYS_EMPTY_MEANS_UNSET = ['fontFamily'];

function mergeFormat(defaults, stored = {}) {
  const format = { ...defaults, ...stored };
  FORMAT_KEYS_EMPTY_MEANS_UNSET.forEach((key) => {
    if (!format[key]) format[key] = defaults[key];
  });
  return format;
}

/**
 * Saved element state merged over the catalogue defaults.
 *
 * Defaults go down first so a newly catalogued element is complete, then the
 * saved values overlay them field by field. `format` and `fields` merge one level
 * deeper for the same reason: a saved element carrying one format flag must not
 * wipe the other four.
 *
 * A saved element the catalogue no longer knows about is dropped rather than
 * carried — it has no name, purpose or fields to render.
 */
/**
 * The first version in which `enabled` means something.
 *
 * Before it, every element defaulted to on and the toggle changed nothing — the
 * structure was a saved design that no export read. So a v1 document's "on" means
 * "nobody was ever asked", not "put this in my book", and honouring it would print
 * twenty-four sections of placeholder text the moment the composer went live.
 * Their formatting and text are real work and are kept; only the flags are
 * dropped. Kept in step with STRUCTURE_VERSION in backend/routes/drafts.py.
 */
const STRUCTURE_VERSION_ENABLED_IS_A_CHOICE = 2;

export function mergeStructure(saved) {
  const merged = defaultStructureState();
  const savedElements = saved?.elements || {};
  const trustEnabled = (saved?.version || 0) >= STRUCTURE_VERSION_ENABLED_IS_A_CHOICE;

  Object.keys(merged).forEach((id) => {
    const stored = savedElements[id];
    if (!stored) return;
    merged[id] = {
      ...merged[id],
      ...stored,
      enabled: trustEnabled ? Boolean(stored.enabled) : merged[id].enabled,
      format: mergeFormat(merged[id].format, stored.format),
      fields: { ...merged[id].fields, ...(stored.fields || {}) },
    };
  });

  return merged;
}

/**
 * The element the Prev / Next page buttons should land on.
 *
 * Walks only the elements that are **enabled**, because the preview is a picture
 * of the finished book — turning to a page the author has excluded would
 * contradict the thing it is showing them. Wraps at both ends.
 *
 * The awkward case is a current element that is itself excluded: it is not in the
 * list being walked, so there is no "next one along" to step from. Next goes to
 * the first page of the book and Prev to the last, which is the same thing the
 * buttons do from a standing start.
 *
 * @param {Array} pages    elements in book order, already filtered to one tool
 * @param {Object} structure  element state, keyed by id
 * @param {string} currentId
 * @param {number} delta   +1 for next, -1 for previous
 * @returns {string|null}  the element id to select, or null if the book is empty
 */
/**
 * Categories the editor's Elements panel lists structure elements from.
 *
 * Body matter is deliberately absent. Those five — prologue, parts and
 * chapters, main text, epilogue, conclusion — *are* the document the author is
 * looking at; a "Main Text" row beside their scenes would name the window they
 * are standing in. They are also the five the composer never prints, so a row
 * would promise a page that does not exist. Front and back matter are the pages
 * that live only on the Structure page, and those are the ones worth surfacing.
 */
const EDITOR_ROW_CATEGORIES = ['front', 'back'];

/**
 * The structure elements the editor's Elements panel shows, in book order.
 *
 * Switched-on only: "added" on the Structure page means switched on, and an
 * excluded element is not part of the book the panel is describing.
 *
 * Goes through mergeStructure rather than reading `saved.elements` directly,
 * because a pre-v2 draft's `enabled: true` means "nobody was ever asked" — read
 * raw, every draft written before the toggle meant anything would list all
 * twenty-six at once.
 *
 * Returns the catalogue's own name and category, not the saved state: these
 * rows are read-only, and everything they display is a property of the element
 * rather than of the author's copy of it.
 */
export function editorStructureRows(saved) {
  const structure = mergeStructure(saved);

  return MANUSCRIPT_ELEMENTS
    .filter((el) => EDITOR_ROW_CATEGORIES.includes(el.category))
    .filter((el) => structure[el.id]?.enabled)
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((el) => ({ id: el.id, name: el.name, category: el.category }));
}

export function nextEnabledElementId(pages, structure, currentId, delta) {
  const enabled = pages.filter((el) => structure[el.id]?.enabled);
  if (!enabled.length) return null;

  const at = enabled.findIndex((el) => el.id === currentId);
  if (at < 0) return (delta > 0 ? enabled[0] : enabled[enabled.length - 1]).id;

  return enabled[(at + delta + enabled.length) % enabled.length].id;
}

// ---------------------------------------------------------------------------
// The Table of Contents
//
// The printed book measures its contents list from the rendered body
// (`outline_headings` and `toc_entries_from_outline` in
// backend/utils/manuscript_render.py). The Structure page has no editor mounted
// and no rendered book, so it reads the same list off the stored manuscript
// instead — everything but the page numbers, which exist only once the book is
// printed and are not guessed at here.

// A chapter and the sections inside it. Deeper headings are not listed.
const CONTENTS_DEPTH = 2;
// The tags `outline_headings` (backend/utils/manuscript_render.py) rewrites.
const PRINTABLE_HEADING = /^(P|H[1-6])$/;

/**
 * The manuscript's chapters and sections, in order, from its stored HTML.
 *
 * Read by `getHeadingBlocks`, the function behind the editor's CHAPTERS panel, so
 * the two cannot disagree: an empty heading is skipped, and a heading cut at a
 * page break is one entry.
 *
 * A section before the first chapter is listed as a chapter. The print nests
 * by heading, and with no chapter above it there is nothing to indent it under.
 *
 * Only a heading stored as a `<p>` or an `<h1>`–`<h6>` is listed, the tags
 * `outline_headings` can turn into print headings. Any other — a `<div>` from
 * drafts older than the heading model, most likely — is in the CHAPTERS panel
 * but cannot print, and this list is a promise about the print.
 *
 * @param {string} content the draft's `content`
 * @returns {Array<{title: string, level: 1|2}>}
 */
export function manuscriptContents(content) {
  if (!content || typeof content !== 'string') return [];

  const doc = new DOMParser().parseFromString(content, 'text/html');
  // getHeadingBlocks reads page bodies. A manuscript saved before the editor
  // paginated is one page, which is what the editor makes of it on load.
  if (!doc.querySelector('.rv-page-body')) {
    doc.body.innerHTML = `<section class="rv-page"><div class="rv-page-body">${doc.body.innerHTML}</div></section>`;
  }

  let seenChapter = false;
  return getHeadingBlocks(doc.body)
    .filter((heading) => heading.level <= CONTENTS_DEPTH && PRINTABLE_HEADING.test(heading.el.tagName))
    .map((heading) => {
      const level = heading.level === 1 || !seenChapter ? 1 : 2;
      if (heading.level === 1) seenChapter = true;
      return { title: heading.text.replace(/\s+/g, ' '), level };
    });
}

// The heading each kind of back-matter page prints, which is what the printed
// contents list picks up. Mirrors `_element_body` in
// backend/utils/manuscript_render.py: a render type missing here prints no
// heading there either.
const BACK_MATTER_HEADING = {
  list: (element) => element.name,
  bodytext: (element) => element.name.replace(' (Alternate)', ''),
  chapter: (element, fields) => (element.chapterHeadingOnly ? fields.heading : fields.chaptitle),
  glossary: (element) => element.name,
  authorbio: (element) => element.name,
  guide: (element) => element.name,
  'cover-spec': (element) => element.name,
};

/**
 * The back matter the author switched on, as contents entries, in book order.
 * The print lists these after the chapters, at chapter level.
 *
 * @param {object} structure element state by id, as `mergeStructure` returns it
 * @returns {Array<{title: string, level: 1}>}
 */
export function backMatterContents(structure) {
  return MANUSCRIPT_ELEMENTS
    .filter((el) => el.category === 'back' && structure?.[el.id]?.enabled)
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((el) => String(BACK_MATTER_HEADING[el.preview]?.(el, structure[el.id].fields || {}) || '').trim())
    .filter(Boolean)
    .map((title) => ({ title, level: 1 }));
}
